import type {
  ClonePdfTemplateRequest,
  ClonePdfTemplateResponse,
  CreatePdfLayoutInput,
  CreatePdfSectionInput,
  PdfLayoutType,
  PdfPageComponent,
  PdfPageLayoutWithComponents,
  PdfSection,
  PdfSectionWithPages,
  PdfTemplateAttachment,
  PdfTemplatePageWithComponents,
  ReorderPdfTemplateAttachmentsInput,
  UpdatePdfSectionInput,
  UpdatePdfTemplateAttachmentInput,
  UpdatePdfTemplateStyleInput,
  UpdatePdfTemplateStyleResponse,
} from "@repo/shared/types";
import { del, get, patch, post, postFormData, put } from "@/lib/api/_client";
import type {
  AddPdfComponentInput,
  CreatePdfTemplateApiInput,
  CreatePdfTemplateFromTemplateInput,
  PdfCompletedSession,
  PdfFlowField,
  PdfPreviewResponse,
  PdfTemplateDetail,
  PdfTemplateListItem,
  ReorderColumnComponentsInput,
  ReorderPageItemsInput,
  SuccessResponse,
  UpdatePdfComponentInput,
  UpdatePdfComponentOrderInput,
  UpdatePdfLayoutInput,
  UpdatePdfTemplateSettingsInput,
} from "@/lib/types/pdf-template";

const BASE = "/internal/pdf-templates";

// --- Templates CRUD ---

export function list(): Promise<PdfTemplateListItem[]> {
  return get(BASE);
}

export function getById(id: string): Promise<PdfTemplateDetail> {
  return get(`${BASE}/${id}`);
}

export function getByFlow(flowId: string): Promise<PdfTemplateListItem[]> {
  return get(`${BASE}/by-flow/${flowId}`);
}

export function create(data: CreatePdfTemplateApiInput): Promise<PdfTemplateDetail> {
  return post(BASE, data);
}

/** QL-334 — deep-copy a template onto another flow (optionally translated). */
export function clone(
  id: string,
  data: ClonePdfTemplateRequest,
): Promise<ClonePdfTemplateResponse> {
  return post<ClonePdfTemplateResponse>(
    `${BASE}/${id}/clone`,
    data satisfies ClonePdfTemplateRequest,
  );
}

export function createFromTemplate(
  data: CreatePdfTemplateFromTemplateInput,
): Promise<PdfTemplateDetail> {
  return post(`${BASE}/from-template`, data);
}

export function updateSettings(
  id: string,
  data: UpdatePdfTemplateSettingsInput,
): Promise<SuccessResponse> {
  return patch(`${BASE}/${id}/settings`, data);
}

export function updateStyle(
  id: string,
  data: UpdatePdfTemplateStyleInput,
): Promise<UpdatePdfTemplateStyleResponse> {
  return patch<UpdatePdfTemplateStyleResponse>(
    `${BASE}/${id}/style`,
    data satisfies UpdatePdfTemplateStyleInput,
  );
}

export function remove(id: string): Promise<SuccessResponse> {
  return del(`${BASE}/${id}`);
}

// --- Template Data ---

export function getTemplatePage(templateId: string): Promise<PdfTemplatePageWithComponents> {
  return get(`${BASE}/${templateId}/page`);
}

export function getFlowFields(templateId: string): Promise<PdfFlowField[]> {
  return get(`${BASE}/${templateId}/flow-fields`);
}

export function getCompletedSessions(templateId: string): Promise<PdfCompletedSession[]> {
  return get(`${BASE}/${templateId}/completed-sessions`);
}

// --- Sections ---

export function getSections(templateId: string): Promise<PdfSectionWithPages[]> {
  return get(`${BASE}/${templateId}/sections`);
}

export function getSectionPage(sectionId: string): Promise<PdfTemplatePageWithComponents> {
  return get(`${BASE}/sections/${sectionId}/page`);
}

// QP-208 — all pages of a section, ordered (multi-page sections from code templates)
export function getSectionPages(sectionId: string): Promise<PdfTemplatePageWithComponents[]> {
  return get(`${BASE}/sections/${sectionId}/pages`);
}

export function createSection(
  templateId: string,
  data: CreatePdfSectionInput,
): Promise<PdfSectionWithPages> {
  return post(`${BASE}/${templateId}/sections`, data);
}

export function updateSection(
  sectionId: string,
  data: UpdatePdfSectionInput,
): Promise<PdfSectionWithPages> {
  return patch(`${BASE}/sections/${sectionId}`, data);
}

export function deleteSection(sectionId: string): Promise<SuccessResponse> {
  return del(`${BASE}/sections/${sectionId}`);
}

// Add a per_portfolio sub-node to an existing goal section (QP-150).
export function createPortfolioSubNode(goalSectionId: string): Promise<PdfSection> {
  return post(`${BASE}/sections/${goalSectionId}/portfolio-subnode`, {});
}

export function reorderSections(
  templateId: string,
  data: { sectionIds: string[] },
): Promise<SuccessResponse> {
  return put(`${BASE}/${templateId}/sections/reorder`, data);
}

// --- Components ---

export function addComponent(
  pageId: string,
  data: AddPdfComponentInput,
): Promise<PdfPageComponent> {
  return post(`${BASE}/pages/${pageId}/components`, data);
}

export function updateComponent(
  componentId: string,
  data: UpdatePdfComponentInput,
): Promise<PdfPageComponent> {
  return patch(`${BASE}/components/${componentId}`, data);
}

export function updateComponentOrder(
  componentId: string,
  data: UpdatePdfComponentOrderInput,
): Promise<SuccessResponse> {
  return patch(`${BASE}/components/${componentId}/order`, data);
}

export function deleteComponent(componentId: string): Promise<SuccessResponse> {
  return del(`${BASE}/components/${componentId}`);
}

export function duplicateComponent(componentId: string): Promise<PdfPageComponent> {
  return post(`${BASE}/components/${componentId}/duplicate`, {});
}

export function reorderItems(
  pageId: string,
  data: ReorderPageItemsInput,
): Promise<SuccessResponse> {
  return put(`${BASE}/pages/${pageId}/items/reorder`, data);
}

export function moveComponentToStandalone(
  componentId: string,
  data: { targetIndex: number },
): Promise<SuccessResponse> {
  return put(`${BASE}/components/${componentId}/move-to-standalone`, data);
}

// --- Layouts ---

export function createLayout({
  pageId,
  layoutType,
}: {
  pageId: string;
  layoutType?: PdfLayoutType;
}): Promise<PdfPageLayoutWithComponents> {
  return post<PdfPageLayoutWithComponents>(`${BASE}/pages/${pageId}/layouts`, {
    layoutType,
  } satisfies CreatePdfLayoutInput);
}

export function deleteLayout(layoutId: string): Promise<SuccessResponse> {
  return del(`${BASE}/layouts/${layoutId}`);
}

export function updateLayout(
  layoutId: string,
  data: UpdatePdfLayoutInput,
): Promise<PdfPageLayoutWithComponents> {
  return patch(`${BASE}/layouts/${layoutId}`, data);
}

export function reorderColumnComponents(
  layoutId: string,
  column: string,
  data: ReorderColumnComponentsInput,
): Promise<SuccessResponse> {
  return put(`${BASE}/layouts/${layoutId}/columns/${column}/reorder`, data);
}

// --- Attachments (QP-220) ---

export function getAttachments(templateId: string): Promise<PdfTemplateAttachment[]> {
  return get(`${BASE}/${templateId}/attachments`);
}

export function uploadAttachment(templateId: string, file: File): Promise<PdfTemplateAttachment> {
  const formData = new FormData();
  formData.append("file", file);
  return postFormData(`${BASE}/${templateId}/attachments`, formData);
}

export function updateAttachment(
  attachmentId: string,
  data: UpdatePdfTemplateAttachmentInput,
): Promise<PdfTemplateAttachment> {
  return patch(`${BASE}/attachments/${attachmentId}`, data);
}

export function deleteAttachment(attachmentId: string): Promise<SuccessResponse> {
  return del(`${BASE}/attachments/${attachmentId}`);
}

export function reorderAttachments(
  templateId: string,
  data: ReorderPdfTemplateAttachmentsInput,
): Promise<SuccessResponse> {
  return put(`${BASE}/${templateId}/attachments/reorder`, data);
}

// --- PDF Generation ---

export function generatePreview(data: {
  templateId: string;
  flowInstanceId: string;
}): Promise<PdfPreviewResponse> {
  return post("/internal/pdf-generation/preview", data);
}
